import {
  setActiveRole,
  setCurrentDepartmentId,
  setCurrentUserInfo,
  setLinkedDepartmentInCache,
} from "../redux/app";

import { GET_DEPARTMENT } from "../../app/javascript/components/ProviderPortalQueries";
import { gql } from "../../app/javascript/components/__generated__/gql";
import { identifyUser } from "../../app/javascript/useAnalytics";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch } from "react-redux";
import { useLazyQuery } from "@apollo/client";
import { useQuery } from "@apollo/client";

const GET_CURRENT_USER_INFO = gql(`
  query GET_CURRENT_USER_INFO {
    currentUserInfo {
      id
      email
      role
      roles
      userSettings {
        name
        phone
        emailNotificationsEnabled
        emailOnlyUserCreatedOrders
        createdEmailNotificationsEnabled
        inTransitEmailNotificationsEnabled
        deliveredEmailNotificationsEnabled
      }
      userDepartments {
        id
        name
        typeId
        typeName
        hospitalId
        hospitalName
        hospitalUsageType
        hospitalTimezone
        hospitalSystem {
          id
          name
          sharedPatients
        }
        faxNotificationsEnabled
        aiExtractionEnabled
        createdAt
        address1
        address2
        city
        stateAbbr
        zipcode
        isHomeHealth
        hospitalPhoneNumber
        allowSignersWithoutFaxNumbers
        cpapFormEnabled
        isZingPreferred
        miscellaneousFormEnabled
        ostomyFormEnabled
        urologyFormEnabled
      }
      authProvider
      permissions
      canViewPayorMembers
    }
  }
`);

export default function useAuth() {
  // Hooks
  const dispatch = useDispatch();

  // Wait for the Auth0 SDK to finish initializing (and, on the login callback,
  // to finish the code-for-token exchange) before firing GET_CURRENT_USER_INFO.
  // Otherwise the query can race ahead of the access token and go out without an
  // Authorization header, resolving currentUserInfo=null and stranding the user
  // logged-out. This surfaces under Firefox Enhanced Tracking Protection, which
  // slows Auth0's silent token acquisition enough to lose the race.
  const { isLoading: auth0IsLoading } = useAuth0();

  // Queries
  const [getDepartment] = useLazyQuery(GET_DEPARTMENT);
  const { loading: queryLoading, error: queryError } = useQuery(
    GET_CURRENT_USER_INFO,
    {
      skip: auth0IsLoading,
      fetchPolicy: "no-cache",
      onCompleted: async (data) => {
        if (!data.currentUserInfo) {
          return;
        }

        dispatch(setCurrentUserInfo(data.currentUserInfo));
        // localStorage departmentId is the departmentId that the user last visited,
        // could be switched from a different tab or switch location
        const localStorageDepartmentId = parseInt(
          localStorage.getItem("departmentId") || "0",
        );

        // utilized for fax/order transcription
        const switchToDepartmentIdQueryParam = new URLSearchParams(
          window.location.search,
        ).get("switch_to_department_id");
        const switchToDepartmentId = switchToDepartmentIdQueryParam
          ? parseInt(switchToDepartmentIdQueryParam)
          : null;

        const desiredDepartmentId =
          switchToDepartmentId || localStorageDepartmentId;

        let departmentId: number;
        if (
          data.currentUserInfo.userDepartments.find(
            (department) => department.id === desiredDepartmentId,
          )
        ) {
          departmentId = desiredDepartmentId;
          dispatch(setCurrentDepartmentId(departmentId));
        } else {
          const departmentLookup = await getDepartment({
            variables: { departmentId: desiredDepartmentId },
          });
          if (departmentLookup.data?.department?.id) {
            departmentId = departmentLookup.data.department.id;
            dispatch(
              setLinkedDepartmentInCache(departmentLookup.data.department),
            );
          } else {
            console.error("departmentLookupError", departmentLookup.error);
            departmentId = data.currentUserInfo.userDepartments[0]?.id || 0;
          }
          dispatch(setCurrentDepartmentId(departmentId));
        }

        const roles = data.currentUserInfo.roles || [];
        const storedRole = localStorage.getItem("activeRole");
        if (storedRole && roles.includes(storedRole)) {
          dispatch(setActiveRole(storedRole));
        } else if (roles.length > 0) {
          // Analytics is an add-on portal for execs who also have a clinician portal, so never default to it when a primary role exists.
          dispatch(
            setActiveRole(roles.find((r) => r !== "analytics") ?? roles[0]),
          );
        }

        identifyUser(data.currentUserInfo.id, {
          name:
            data.currentUserInfo.userSettings.name ||
            data.currentUserInfo.email,
          email: data.currentUserInfo.email,
        });
      },
    },
  );

  // Keep the app in its loading state (App.tsx render gate) while Auth0 is still
  // initializing so it doesn't paint the logged-out home before auth resolves.
  const authIsLoading = auth0IsLoading || queryLoading;
  const authError = queryError;

  return {
    authIsLoading,
    authError,
  };
}
